import JSONAPISerializer from '@ember-data/serializer/json-api';
import { pluralize } from '@warp-drive/utilities/string';
import { isArray } from '@ember/array';

// Minimum shape of a model record as reached from an Ember Data adapter
// Snapshot. Upstream ember-data exposes this via Snapshot<T>, but the types
// are currently unresolvable here (see #7505). These fields are what the
// serializer actually reads at runtime.
interface SerializerModelRecord {
  id: string;
  constructor: { modelName: string };
  relationshipFor: (key: string) => { type: string };
  [key: string]: unknown;
}

type Snapshot = {
  adapterOptions: {
    createHasMany?: SerializerModelRecord[];
    destroyHasMany?: SerializerModelRecord[];
    updateRelationship?: string;
    changelog?: string;
  };
  record: SerializerModelRecord;
};

export default class ApplicationSerializer extends JSONAPISerializer {
  serialize(snapshot: Snapshot, options: unknown) {
    const { adapterOptions } = snapshot;

    if (adapterOptions && Array.isArray(adapterOptions.createHasMany)) {
      const [firstRecord] = adapterOptions.createHasMany;
      if (firstRecord) {
        const type = pluralize(firstRecord.constructor.modelName);
        const data = adapterOptions.createHasMany.map((record) => {
          return { type, id: record.id };
        });

        return { data };
      }
    }

    if (adapterOptions && isArray(adapterOptions.destroyHasMany)) {
      const [firstRecord] = adapterOptions.destroyHasMany;
      if (firstRecord) {
        const type: string = pluralize(firstRecord.constructor.modelName);
        const data = adapterOptions.destroyHasMany.map((record) => {
          return { type, id: record.id };
        });

        return { data };
      }
    }

    if (adapterOptions?.updateRelationship) {
      const type = pluralize(
        snapshot.record.relationshipFor(adapterOptions.updateRelationship).type
      );
      const relatedRecord = snapshot.record[adapterOptions.updateRelationship] as {
        get: (key: string) => unknown;
      };
      return {
        data: {
          type,
          id: relatedRecord.get('id'),
        },
      };
    }

    const serialized = super.serialize(snapshot, options);

    // If a changelog string is sent in the adapterOptions, send it as an attribute to the API
    if (adapterOptions?.changelog) {
      serialized.data.attributes._changelog = adapterOptions.changelog;
    }

    return serialized;
  }

  serializeHasMany(snapshot: unknown, json: unknown, relationship: { key: string }) {
    const key = relationship.key;
    if (key === 'versions') {
      return;
    } else {
      super.serializeHasMany(snapshot, json, relationship);
    }
  }
}
